import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { m } from "framer-motion";
import { ArrowRight, Phone, Clock, MapPin, Calendar } from "lucide-react";
import { api } from "../lib/api";
import { NAP } from "../lib/nap";
import InstagramFeed from "../components/InstagramFeed";
import SEO from "../components/SEO";
import { useSkipIntroAnimation } from "../lib/prerender";

// Unica foto reale del salone (panoramica interna). Aggiungere qui altre foto quando il cliente le fornisce.
const HERO_SLIDES = ["/assets/hero/salone.jpg"];

function useCountUp(target, duration = 2000, start = 0) {
  const [val, setVal] = useState(start);
  useEffect(() => {
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(start + (target - start) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return val;
}

function Stat({ to, suffix = "+", label, delay = 0 }) {
  const [active, setActive] = useState(false);
  useEffect(() => { const t = setTimeout(() => setActive(true), delay); return () => clearTimeout(t); }, [delay]);
  const val = useCountUp(active ? to : 0);
  return (
    <m.div initial={{ opacity: 0.6, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center" data-testid="stat-counter">
      <div className="font-display text-5xl lg:text-7xl text-[#C9A961] tabular-nums">{suffix}{val}</div>
      <div className="font-display-sc text-[10px] tracking-[0.4em] text-white/60 mt-3">{label}</div>
    </m.div>
  );
}

export default function Home() {
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [news, setNews] = useState([]);
  const [content, setContent] = useState({});
  const [slide, setSlide] = useState(0);
  // Niente fade-in del hero al primo mount su pagina prerenderizzata (evita il blink)
  const skipIntro = useSkipIntroAnimation();

  useEffect(() => {
    (async () => {
      const [s, g, n, pc] = await Promise.all([
        api.get("/services").catch(() => ({ data: [] })),
        api.get("/gallery").catch(() => ({ data: [] })),
        api.get("/news").catch(() => ({ data: [] })),
        api.get("/page-content").catch(() => ({ data: [] })),
      ]);
      setServices(s.data);
      setGallery(g.data);
      setNews(n.data);
      const m = {}; (pc.data || []).forEach((p) => (m[p.key] = p.content));
      setContent(m);
    })();
    if (HERO_SLIDES.length < 2) return undefined;
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="home-page" className="bg-[#0a0a0a] text-[#F2EFE9] relative overflow-hidden">
      <SEO
        title=""
        description="Polverini Hair System di Itria Leone, parrucchiere ed estetica a Palazzolo Acreide (SR) in Via Bando Superiore 49: taglio Sculpture, colore biologico, unghie, pedicure, epilazione."
        path="/"
      />
      {/* HERO with slideshow + centered logo */}
      <section data-testid="hero-section" className="relative min-h-[100vh] flex items-center justify-center overflow-hidden vignette">
        {HERO_SLIDES.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            loading={i === 0 ? "eager" : "lazy"}
            fetchpriority={i === 0 ? "high" : "auto"}
            className={`slide-img ${i === slide ? "active" : ""}`}
          />
        ))}
        <div className="absolute inset-0 bg-[#0a0a0a]/65" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/40 via-transparent to-[#0a0a0a]" />

        <m.div
          initial={skipIntro ? false : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 text-center px-6 max-w-5xl"
        >
          <img
            src="/assets/logo-palazzolo.png"
            alt="Beauty Salon by Itria Leone"
            className="h-24 lg:h-32 w-auto mx-auto mb-8"
            style={{ filter: "drop-shadow(0 0 24px rgba(201,169,97,0.35))" }}
            data-testid="hero-logo"
          />
          <div className="font-display-sc text-[11px] lg:text-[12px] tracking-[0.5em] text-[#C9A961] mb-6">
            PALAZZOLO ACREIDE — SIRACUSA
          </div>
          {/* Un solo h1 semantico: le due righe grafiche sono span a blocco (.hero-name applica già
              text-transform: uppercase); la qualifica "Parrucchiere a Palazzolo Acreide" è solo per
              screen reader e motori di ricerca (sr-only). */}
          <h1 className="hero-name text-5xl md:text-7xl lg:text-[7.5rem]" data-testid="hero-title">
            <span className="block mb-2">Polverini</span>
            <span className="block text-[#C9A961] -mt-2 lg:-mt-5">Hair System</span>
            <span className="sr-only"> di Itria Leone — parrucchiere ed estetica a Palazzolo Acreide: taglio, colore biologico, unghie ed epilazione</span>
          </h1>
          <div className="hero-script text-3xl md:text-5xl text-white/85 mt-6 mb-10">
            by Itria Leone
          </div>

          <div className="font-elegant text-xl md:text-2xl text-white/75 max-w-2xl mx-auto leading-relaxed italic">
            {content.hero_description ||
              "Parrucchiere ed estetica a Palazzolo Acreide. Ci prendiamo cura di te dalla testa ai piedi."}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/prenotazioni"
              data-testid="hero-booking-btn"
              className="group inline-flex items-center justify-center gap-3 bg-[#C9A961] text-[#0a0a0a] px-10 py-5 text-[11px] uppercase tracking-[0.32em] font-semibold hover:bg-[#E0C087] transition-all duration-500 hover:tracking-[0.4em]"
            >
              Prenota un appuntamento
              <ArrowRight size={14} className="group-hover:translate-x-1 transition" />
            </Link>
            <a
              href={`https://wa.me/${NAP.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp"
              className="inline-flex items-center justify-center gap-3 border border-white/30 hover:border-[#C9A961] text-white/90 hover:text-[#C9A961] px-10 py-5 text-[11px] uppercase tracking-[0.32em] transition-all duration-500"
            >
              WhatsApp {NAP.mobile}
            </a>
          </div>
        </m.div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40 text-[10px] uppercase tracking-[0.4em]">
          <span>Scroll</span>
          <span className="w-px h-12 bg-gradient-to-b from-[#C9A961] to-transparent" />
        </div>
      </section>

      {/* INFO STRIP */}
      <section className="border-y border-[#C9A961]/15 py-8 bg-[#0a0a0a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          {[
            { icon: Phone, title: "Telefono", text: NAP.landline, href: NAP.landlineHref },
            { icon: MapPin, title: "Salone", text: NAP.address, href: NAP.mapsUrl, external: true },
            { icon: Clock, title: "Orari", text: NAP.hoursShort },
          ].map((it, i) => (
            <a key={i} href={it.href || "#"} target={it.external ? "_blank" : undefined} rel={it.external ? "noreferrer" : undefined} className="group flex items-center justify-center gap-4">
              <it.icon className="text-[#C9A961]" size={18} />
              <div className="text-left">
                <div className="font-display-sc text-[10px] tracking-[0.35em] text-white/50">{it.title}</div>
                <div className="font-display text-base group-hover:text-[#C9A961] transition">{it.text}</div>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* INTRO — chi siamo centrato */}
      <section data-testid="about-intro" className="py-32 lg:py-40 relative">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="gold-divider">
            <span className="font-display-sc text-[10px] tracking-[0.4em] text-[#C9A961]">Chi Siamo</span>
          </div>
          <h2 className="font-display text-4xl md:text-6xl mt-8 leading-[1.1]">
            Parrucchieri e
            <br />
            <span className="hero-script text-5xl md:text-7xl text-[#C9A961]">curatori d'immagine</span>
            <br />
            da oltre vent'anni
          </h2>
          <div className="font-elegant text-xl md:text-2xl text-white/70 mt-10 leading-[1.7] italic">
            <p>
              Il nostro obiettivo è migliorare la salute, l'aspetto e il benessere dei capelli e, in generale, l'immagine di chi si affida a noi. Siamo presenti sul territorio siracusano <em>da oltre vent'anni</em>.
            </p>
            <p className="mt-6">
              Da sempre legati alla <strong className="text-white not-italic font-display">Polverini Academy</strong>, detentrice dell'unica tecnica di taglio 100% made in Italy "Sculpture": tagli come sculture.
            </p>
          </div>
          <Link
            to="/chi-siamo"
            data-testid="home-about-link"
            className="inline-flex items-center gap-3 mt-12 border border-[#C9A961] text-[#C9A961] px-10 py-4 text-[11px] uppercase tracking-[0.32em] hover:bg-[#C9A961] hover:text-[#0a0a0a] transition-all duration-500"
          >
            Scopri la nostra storia <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* STATS */}
      <section data-testid="stats-section" className="py-20 lg:py-28 border-y border-[#C9A961]/15 bg-[#080808]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-10">
          <Stat to={20} label="Anni di esperienza" />
          <Stat to={5} suffix="" delay={150} label="Linee bio Biobotanic" />
          <Stat to={9} suffix="" delay={300} label="Servizi capelli ed estetica" />
          <Stat to={100} suffix="" delay={450} label="% made in Italy" />
        </div>
      </section>

      {/* SERVICES — grid centrato magazine style */}
      <section data-testid="services-section" className="py-28 lg:py-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-20">
            <div className="gold-divider">
              <span className="font-display-sc text-[10px] tracking-[0.4em] text-[#C9A961]">Cosa offriamo</span>
            </div>
            <h2 className="font-display text-5xl md:text-7xl mt-8">
              Alta Qualità per <span className="hero-script text-[#C9A961]">i tuoi desideri</span>
            </h2>
            <p className="font-elegant text-xl text-white/70 mt-6 max-w-2xl mx-auto italic">
              Dal taglio Sculpture al colore biologico, dalle unghie all'epilazione. Ci prendiamo cura di te dalla testa ai piedi.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((s, i) => (
              <m.div
                key={s.id}
                initial={{ opacity: 0.6, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="group relative border border-[#2a2a2a] hover:border-[#C9A961] bg-[#0d0d0d] transition-all duration-500"
                data-testid={`service-card-${i}`}
              >
                <div className="aspect-[4/3] zoom-wrap relative overflow-hidden">
                  {s.image && <img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover opacity-80 group-hover:opacity-100" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/30 to-transparent" />
                  <div className="absolute bottom-4 left-4 font-display-sc text-[9px] tracking-[0.4em] text-[#C9A961]">
                    {s.category}
                  </div>
                </div>
                <div className="p-7 text-center">
                  <h3 className="font-display text-2xl">{s.title}</h3>
                  <div className="w-12 h-px bg-[#C9A961] mx-auto my-4 opacity-60" />
                  <p className="font-elegant text-base text-white/65 italic leading-relaxed line-clamp-3">{s.description}</p>
                  {s.price && <div className="font-display text-xl text-[#C9A961] mt-5">{s.price}</div>}
                </div>
              </m.div>
            ))}
          </div>

          <div className="text-center mt-14">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/servizi"
                data-testid="all-services-link"
                className="inline-flex items-center gap-3 border border-[#C9A961] text-[#C9A961] px-10 py-4 text-[11px] uppercase tracking-[0.32em] hover:bg-[#C9A961] hover:text-[#0a0a0a] transition"
              >
                Tutti i servizi <ArrowRight size={14} />
              </Link>
              <Link
                to="/servizi/listino"
                data-testid="home-listino-link"
                className="inline-flex items-center gap-3 border border-white/30 text-white/80 px-10 py-4 text-[11px] uppercase tracking-[0.32em] hover:border-[#C9A961] hover:text-[#C9A961] transition"
              >
                Listino prezzi <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US — centrato */}
      <section data-testid="why-section" className="py-28 lg:py-36 bg-[#080808] border-y border-[#C9A961]/15">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="gold-divider">
              <span className="font-display-sc text-[10px] tracking-[0.4em] text-[#C9A961]">Perché sceglierci</span>
            </div>
            <h2 className="font-display text-4xl md:text-6xl mt-8">
              Per la <span className="hero-script text-[#C9A961]">cura sartoriale</span>
              <br />che mettiamo in tutto
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { n: "01", t: "Taglio Sculpture", d: "La tecnica di taglio Polverini, l'unica 100% made in Italy: tagli come sculture, costruiti sulla forma del viso." },
              { n: "02", t: "Prodotti biologici", d: "Linee Biobotanic certificate ICEA: colore senza ammoniaca, cura e styling senza sostanze aggressive." },
              { n: "03", t: "Dalla testa ai piedi", d: "Capelli, unghie, pedicure, epilazione e trucco nello stesso salone, con un metodo preciso che nulla lascia al caso." },
            ].map((b, i) => (
              <div key={i} className="text-center p-8 border border-[#2a2a2a] hover:border-[#C9A961] transition-colors group">
                <div className="font-display text-[#C9A961] text-5xl">{b.n}</div>
                <div className="w-8 h-px bg-[#C9A961] mx-auto my-5 opacity-60" />
                <div className="font-display text-2xl">{b.t}</div>
                <p className="font-elegant text-base text-white/65 mt-4 italic leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAGAZINE — Disabled (can be re-enabled by re-adding this section). Admin /admin/news still works. */}

      {/* GALLERY — masonry (solo se il pannello ha delle foto) */}
      {gallery.length > 0 && (
      <section data-testid="gallery-preview" className="py-28 bg-[#080808] border-y border-[#C9A961]/15">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="gold-divider">
              <span className="font-display-sc text-[10px] tracking-[0.4em] text-[#C9A961]">I nostri lavori</span>
            </div>
            <h2 className="font-display text-5xl md:text-6xl mt-8">
              Galleria <span className="hero-script text-[#C9A961]">opere</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {gallery.slice(0, 8).map((g, i) => (
              <Link
                key={g.id}
                to="/galleria"
                data-testid={`gallery-preview-${i}`}
                className={`zoom-wrap border border-[#2a2a2a] hover:border-[#C9A961] block ${
                  i === 0 || i === 5 ? "md:row-span-2 aspect-[3/5]" : "aspect-square"
                }`}
              >
                <img src={g.image} alt={g.caption} loading="lazy" className="w-full h-full object-cover" />
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/galleria"
              className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-[#C9A961] hover:tracking-[0.4em] transition-all"
            >
              Vedi tutta la galleria <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* CTA — appointment */}
      <section data-testid="cta-section" className="relative py-32 lg:py-44 overflow-hidden">
        <img
          src="/assets/hero/salone-bw.jpg"
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-[#0a0a0a]/85 to-[#0a0a0a]" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <Calendar className="text-[#C9A961] mx-auto" size={28} />
          <div className="font-display-sc text-[10px] tracking-[0.4em] text-[#C9A961] mt-4">Prenota</div>
          <h2 className="font-display text-5xl md:text-7xl mt-6 leading-[1]">
            Un consulto
            <br />
            <span className="hero-script text-[#C9A961]">gratuito</span> ti aspetta
          </h2>
          <p className="font-elegant text-xl text-white/75 mt-8 italic leading-relaxed">
            Passa dal salone senza impegno: valutiamo insieme cute e capelli e ti consigliamo il servizio e i prodotti giusti per te.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/prenotazioni"
              data-testid="cta-booking"
              className="inline-flex items-center justify-center gap-3 bg-[#C9A961] text-[#0a0a0a] px-10 py-5 text-[11px] uppercase tracking-[0.32em] font-semibold hover:bg-[#E0C087] transition"
            >
              Prenota online <ArrowRight size={14} />
            </Link>
            <a
              href={NAP.landlineHref}
              data-testid="cta-phone"
              className="inline-flex items-center justify-center gap-3 border border-white/30 hover:border-[#C9A961] text-white px-10 py-5 text-[11px] uppercase tracking-[0.32em] transition"
            >
              <Phone size={14} /> {NAP.landline}
            </a>
          </div>
        </div>
      </section>

      {/* INSTAGRAM FEED */}
      <InstagramFeed />

      {/* TESTIMONIALS: sezione rimossa finché non ci sono recensioni reali del salone */}
    </div>
  );
}
